import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, Divider, message, Modal, Space } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type CtsModuleSpecsFragment } from '../../../../../api/fragments/CtsModuleSpecs';
import {
    DeleteCtsModuleSettingDocument,
    type DeleteCtsModuleSettingMutation,
    type DeleteCtsModuleSettingMutationVariables,
} from '../../../../../api/mutations/deleteCtsModuleSetting';

export type CtsSettingActionsProps = {
    module: CtsModuleSpecsFragment;
    setting: CtsModuleSpecsFragment['ctsSettings'][number];
    showUpdate: boolean;
};

const CtsSettingsAction = ({ module, setting, showUpdate }: CtsSettingActionsProps) => {
    const moduleId = module.id;
    const ctsSettingId = setting.id;

    const apolloClient = useApolloClient();
    const navigate = useNavigate();
    const { t } = useTranslation(['ctsModuleDetails', 'moduleDetails']);

    const deleteAction = useCallback(() => {
        Modal.confirm({
            title: t('ctsModuleDetails:settings.deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('ctsModuleDetails:settings.deleteModal.content'),
            okText: t('ctsModuleDetails:settings.deleteModal.submitButton'),
            okType: 'danger',
            cancelText: t('ctsModuleDetails:settings.deleteModal.cancelButton'),
            onOk: async () => {
                message.loading({
                    content: t('ctsModuleDetails:settings.deleteModal.messages.submitting'),
                    key: 'primary',
                    duration: 0,
                });

                await apolloClient
                    .mutate<DeleteCtsModuleSettingMutation, DeleteCtsModuleSettingMutationVariables>({
                        mutation: DeleteCtsModuleSettingDocument,
                        variables: { moduleId, settingId: ctsSettingId },
                    })
                    .finally(() => {
                        message.destroy('primary');
                    });

                message.success({
                    content: t('ctsModuleDetails:settings.deleteModal.messages.success'),
                    key: 'primary',
                });
            },
        });
    }, [t, apolloClient, moduleId, ctsSettingId]);

    return (
        <Space size="small" split={<Divider type="vertical" />}>
            {showUpdate && (
                <Button
                    onClick={() => navigate(`/admin/system/modules/${moduleId}/cts/${ctsSettingId}`)}
                    size="small"
                    style={{ padding: 0 }}
                    type="link"
                >
                    {t('ctsModuleDetails:actions.update')}
                </Button>
            )}
            <Button onClick={deleteAction} size="small" style={{ padding: 0 }} type="link">
                {t('ctsModuleDetails:actions.delete')}
            </Button>
        </Space>
    );
};

export default CtsSettingsAction;
